import supabase from 'modules/core/supabase/supabase';
import { TemplateAccess } from 'modules/manager/domain/template-access';
import { TEMPLATE_NAME_CONFLICT_MESSAGE, isTemplateNameConflict } from 'modules/manager/domain/template-name-conflict';
import { TEMPLATE_RESOLUTIONS } from 'modules/manager/domain/template-resolutions';
import { useCallback, useState } from 'react';
import { UDText } from 'ud-ui/components';
import Modal from 'ud-ui/components/modal';
import MediaManagerTemplateForm, {
  DEFAULT_RESOLUTION_OPTION,
  DEFAULT_TEMPLATE_TYPE_OPTION,
  MediaManagerTemplateFormValues,
} from '../../components/forms/media-manager-template-form';

interface Args {
  tournamentId: number | null;
  existingNames: string[];
  templatesCount: number;
  onCreated: () => void;
  /**
   * Scope of the created template. 'private' (default) — a tournament template;
   * 'default' — a global preset (used on the shared-templates library page).
   */
  access?: TemplateAccess;
  /** Modal title; defaults per access. */
  title?: string;
}

function useCreateTemplateModal({ tournamentId, existingNames, templatesCount, onCreated, access = 'private', title }: Args) {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const open = useCallback(() => {
    setError(null);
    setIsOpen(true);
  }, []);
  const close = useCallback(() => setIsOpen(false), []);

  const onSubmit = useCallback(
    async (values: MediaManagerTemplateFormValues) => {
      // A global preset is tournament-agnostic; only a private template needs an active tournament.
      if (access !== 'default' && !tournamentId) {
        console.error('Cannot create a private template without an active tournament');
        return;
      }
      setIsLoading(true);
      setError(null);
      try {
        const resolution = TEMPLATE_RESOLUTIONS[values.resolution.value];
        await supabase.mediaManager.createTemplate({
          name: values.name.trim(),
          objects: [],
          version: '5.3.0',
          access,
          tournamentId,
          order: templatesCount,
          width: resolution.width,
          height: resolution.height,
          templateType: values.templateType.value,
          templateSubtypes: values.subtype ? [values.subtype.value] : [],
        });
        onCreated();
        close();
      } catch (err) {
        console.error('Failed to create media manager template:', err);
        // The name check above only knows the names this page loaded; the unique
        // rule in the DB is what actually decides.
        setError(isTemplateNameConflict(err) ? TEMPLATE_NAME_CONFLICT_MESSAGE : 'Не удалось создать шаблон, попробуйте ещё раз');
      } finally {
        setIsLoading(false);
      }
    },
    [access, tournamentId, templatesCount, onCreated, close],
  );

  return {
    open,
    close,
    modal: (
      <Modal show={isOpen} onClose={close}>
        <UDText type="title-3" className="mb-5">
          {title ?? (access === 'default' ? 'Создать пресет' : 'Создать шаблон')}
        </UDText>
        <MediaManagerTemplateForm
          initialValues={{
            name: '',
            resolution: DEFAULT_RESOLUTION_OPTION,
            templateType: DEFAULT_TEMPLATE_TYPE_OPTION,
            subtype: null,
          }}
          existingNames={existingNames}
          isLoading={isLoading}
          errorMessage={error}
          onSubmit={onSubmit}
        />
      </Modal>
    ),
  };
}

export default useCreateTemplateModal;
